import { z } from 'zod';
import { I18nStringSchema, MoneySchema, ShortIdSchema, TimestampSchema } from './primitives.js';

/**
 * Challenges — operator-authored, TIME-BOUNDED loyalty goals (the windowed
 * sibling of achievements). Where an achievement measures a LIFETIME stat
 * ("50 washes ever"), a challenge measures progress ONLY inside its
 * `[startAt, endAt)` window ("5 washes in October → free wash"). Progress is
 * tracked per-customer in a separate `challengeProgress` doc that resets
 * naturally: a new challenge is a new doc, so nothing carries across windows.
 *
 * Definitions live at `tenants/{tid}/loyaltyProgram/config/challenges/{id}`
 * (parallel to `config/achievements` + `config/tiers`), public-read /
 * owner-manager-write per firestore.rules `loyaltyProgram/{doc=**}`. There is
 * NO default seed — challenges are always operator-created (unlike the seeded
 * achievement badges), so a fresh tenant simply has an empty subcollection.
 *
 * The award pipeline (`onTicketCollected`) evaluates active challenges POST
 * transaction + best-effort, gated on `enabledFeatures.challenges`.
 */

/**
 * Slice-1 criterion — a discriminated union of the WINDOW-COUNTABLE kinds only.
 * Both are things the loyalty pipeline can tally from a single paid wash's
 * amount, so progress is a running counter inside the window:
 *   - `visits` → count of qualifying paid washes in the window
 *   - `spend`  → integer sen spent in the window
 * Lifetime-only or per-item kinds (streaks / package counts) are intentionally
 * excluded — they aren't windowed-countable and belong to achievements.
 */
export const ChallengeCriterionSchema = z
  .discriminatedUnion('kind', [
    z.object({
      kind: z.literal('visits'),
      n: z
        .number()
        .int()
        .positive()
        .describe('Qualifying paid washes needed within the challenge window'),
    }),
    z.object({
      kind: z.literal('spend'),
      amountSen: MoneySchema.describe('Integer sen the customer must spend within the window'),
    }),
  ])
  .describe('What the customer must do inside the window to complete the challenge');
export type ChallengeCriterion = z.infer<typeof ChallengeCriterionSchema>;

/**
 * Reward granted when a challenge is completed. Mirrors the achievement reward
 * union (points / voucher / none) so the award pipeline can reuse the same
 * grant paths. `points` writes a ledger 'earned' entry + increments the
 * account balance; `voucher` issues an IssuedVoucher via `issueVoucherForCustomer`;
 * `none` is a bragging-rights-only challenge (the completion is the reward).
 */
export const ChallengeRewardSchema = z
  .discriminatedUnion('kind', [
    z.object({
      kind: z.literal('points'),
      amount: z.number().int().positive().describe('Bonus points granted on completion'),
    }),
    z.object({
      kind: z.literal('voucher'),
      voucherTemplateId: ShortIdSchema.describe('Voucher template issued on completion'),
    }),
    z.object({ kind: z.literal('none') }),
  ])
  .describe('What the customer earns when they complete the challenge');
export type ChallengeReward = z.infer<typeof ChallengeRewardSchema>;

export const ChallengeSchema = z
  .object({
    id: ShortIdSchema.describe('Short id; equals the Firestore doc id'),
    name: I18nStringSchema.describe('Operator-authored challenge name (per-locale inline object)'),
    icon: z.string().describe('Emoji or icon glyph shown on the challenge card'),
    description: I18nStringSchema.describe('Operator-authored one-line description (per-locale)'),
    criterion: ChallengeCriterionSchema,
    reward: ChallengeRewardSchema,
    startAt: TimestampSchema.describe('Window opens — progress only counts at/after this instant'),
    endAt: TimestampSchema.describe(
      'Window closes — progress stops counting at this instant (exclusive)',
    ),
    active: z
      .boolean()
      .default(true)
      .describe('Operator kill-switch; an inactive challenge never counts or shows'),
    createdAt: TimestampSchema.describe('When the challenge was created'),
    updatedAt: TimestampSchema.describe('When the challenge was last edited'),
  })
  .describe('A time-bounded, windowed loyalty goal an operator runs')
  .superRefine((val, ctx) => {
    // The window must be non-empty: endAt strictly after startAt. Otherwise the
    // challenge could never be active (isChallengeActive requires start <= now < end).
    const startMs = val.startAt.toMillis?.() ?? val.startAt.seconds * 1000;
    const endMs = val.endAt.toMillis?.() ?? val.endAt.seconds * 1000;
    if (!(endMs > startMs)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'endAt must be after startAt',
        path: ['endAt'],
      });
    }
  });
export type Challenge = z.infer<typeof ChallengeSchema>;

/**
 * Per-customer windowed progress toward one challenge. Lives at
 * `users/{uid}/loyaltyAccounts/{tenantId}/challengeProgress/{challengeId}` —
 * doc id = challengeId, so a customer has at most one progress doc per
 * challenge and the write is naturally idempotent. Server-only write (admin SDK
 * via `onTicketCollected`); the owning customer reads their own via the per-uid
 * self-read rule, parallel to the earned-achievements + points ledger.
 *
 * `earnedAt` is the source of truth for "completed": once set, the pipeline
 * skips the challenge for this customer (no re-award, no further counting).
 */
export const ChallengeProgressSchema = z.object({
  challengeId: ShortIdSchema.describe(
    'Id of the challenge this progress belongs to — equals doc id',
  ),
  visits: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe('Qualifying paid washes counted inside the window so far'),
  spentSen: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe('Integer sen spent inside the window so far'),
  earnedAt: TimestampSchema.optional().describe(
    'When the customer completed the challenge; unset while in progress',
  ),
});
export type ChallengeProgress = z.infer<typeof ChallengeProgressSchema>;
